import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, OverlayTrigger, Row, Tooltip, InputGroup } from "react-bootstrap";
import DommusBarraTitulo from "../../components/DommusBarraTitulo";
import ItemBarraTitulo from "../../components/DommusBarraTitulo/ItemBarraTitulo";
import DommusToggle from "../../components/DommusToggle";
import { CartaoDashboardAtendente } from "../../components/CartaoDashboardAtendente";
import './index.css'
import { DashboardService } from "../../services/DashboardService"
import Swal from "sweetalert2";
import { DommusAlertaBootstrap } from "../../components/DommusAlertaBootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faSearch } from "@fortawesome/free-solid-svg-icons";
import { trackPromise } from "react-promise-tracker";
import calendarOutlineBadged from '@iconify-icons/clarity/calendar-outline-badged';
import DatePicker from "react-date-picker/dist/entry";
import "../PortalCliente/AtendimentoIntervaloDatasPicker/DatePicker.scss";
import { Icon } from "@iconify/react";
import ModalDommus from "../../components/Modal/index";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { TipoAtendimentoContext } from "../../contexts/TipoAtendimentoContext";
import { EmpreendimentoContext } from "../../contexts/EmpreendimentosContext";
import { WebSocketContext } from "../../contexts/WebSocketContext";

function converteParaData(valor) {
  if(!valor) {
    return null
  }
  if(valor instanceof Date) {
    return valor
  }
  return new Date(String(valor).includes('T') ? valor : `${valor}T03:00:00Z`)
}

function converteParaInputDate(valor) {
  const data = converteParaData(valor)

  return data ? data.toISOString().split('T')[0] : ''
}

const somenteDigitos = (valor) => String(valor ?? '').replace(/\D/g, '')

/**
 * Compara contatos pelo final do número: o mesmo telefone circula ora com DDI e DDD, ora sem,
 * e o trecho final é a parte estável entre as duas formas.
 */
function ehMesmoContato(umContato, outroContato) {
  const um = somenteDigitos(umContato)
  const outro = somenteDigitos(outroContato)
  if(um.length < 8 || outro.length < 8) {
    return false
  }

  return um.endsWith(outro.slice(-8)) || outro.endsWith(um.slice(-8))
}

function montaOpcoes(recursos, descricao, valor) {
  return recursos.map(recurso => {
    return {
      label: recurso[descricao],
      value: recurso[valor]
    }
  })
}

export function Dashboard() {
  const [usuarios, setUsuarios] = useState(null)
  const [modalFiltrosAberto, setModalFiltrosAberto] = useState(false)
  const { tiposAtendimento } = useContext(TipoAtendimentoContext)
  const { empreendimentoLista } = useContext(EmpreendimentoContext)
  const { channel } = useContext(WebSocketContext)
  const [estadosLigacao, setEstadosLigacao] = useState({})
  // Situação de ligação que veio nas métricas de cada cartão, reportada por ele: é o que sobra
  // para desligar o badge de quem estava em ligação sem ter estado em tempo real.
  const metricasLigacaoRef = useRef({})
  const registrarMetricasLigacao = useCallback((idUsuario, metricas) => {
    metricasLigacaoRef.current = { ...metricasLigacaoRef.current, [idUsuario]: metricas }
  }, [])
  const [timeoutAtualizar, setTimeoutAtualizar] = useState(null);
  const [filtrosAplicados, setFiltrosAplicados] = useState(null)
  const { handleSubmit, control, setValue, getValues, watch } = useForm({
    defaultValues: {
      dataInicial: new Date(),
      dataFinal: new Date(),
      tipoAtendimento: [],
      empreendimento: []
    }
  })
  const dataInicialSelecionada = watch('dataInicial')
  const dataFinalSelecionada = watch('dataFinal')

  function montaFiltros(dados) {
    return {
      empreendimentos: dados.empreendimento?.map(empreendimento => empreendimento.value) || null,
      tipoAtendimentos: dados.tipoAtendimento?.map(tipoAtendimento => tipoAtendimento.value) || null,
      dataInicial: converteParaData(dados.dataInicial)?.toISOString() || null,
      dataFinal: converteParaData(dados.dataFinal)?.toISOString() || null
    }
  }

  const atualizarUsuarios = (filtros = null) => {

    if(timeoutAtualizar){
      clearTimeout(timeoutAtualizar);
      setTimeoutAtualizar(null);
    }

    const filtrosAtuais = filtros || montaFiltros(getValues())
    setFiltrosAplicados(filtrosAtuais)

    trackPromise(
      DashboardService.buscarListaUsuarios(filtrosAtuais)
    ).then(res => {
        const novoUsuarios = res.data;
        setUsuarios(novoUsuarios)
        // A lista recém buscada é a informação mais recente: os estados acumulados via
        // websocket perdem a validade, inclusive os `null`, que senão continuariam
        // escondendo uma ligação nova do mesmo atendente. Os cartões buscam métricas novas
        // junto com a lista e reportam de novo.
        setEstadosLigacao({})
        metricasLigacaoRef.current = {}
        const novoTimeout = setTimeout(() => {
          atualizarUsuarios();
        }, 600000);
        setTimeoutAtualizar(novoTimeout);
      }).catch(error => {
        const novoTimeout = setTimeout(() => {
          atualizarUsuarios();
        }, 600000);
        setTimeoutAtualizar(novoTimeout);
        Swal.fire('Ooops...', `Houve um erro ao buscar os usuários. Erro: ${error}, Detalhes: ${error.response?.data}`, 'error')

      })
  }

  function atualizarFiltros(dados) {
    setModalFiltrosAberto(false)
    atualizarUsuarios(montaFiltros(dados));
  }

  function alterarPeriodo(data, atributoFiltro) {
    const dadosAtuais = getValues()
    const dataInicial = converteParaData(atributoFiltro === 'dataInicial' ? data : dadosAtuais.dataInicial)
    const dataFinal = converteParaData(atributoFiltro === 'dataFinal' ? data : dadosAtuais.dataFinal)
    if(dataInicial && dataFinal && dataInicial > dataFinal) {
      Swal.fire('Período Inválido!', 'A data inicial deve ser anterior a data final.', 'warning')

      return
    }
    setValue(atributoFiltro, data)
    atualizarUsuarios(montaFiltros({ ...dadosAtuais, [atributoFiltro]: data }));
  }

  useEffect(() => {
    atualizarUsuarios();
  }, [])

  /**
   * Estado de ligação em tempo real, complementando as chaves que vêm na busca dos usuários.
   *
   * Cada evento cobre uma transição: `voip.1` é a oferta e acende o badge laranja no atendente
   * ofertado, `voip-assumido` marca quem atendeu e vira o badge para verde, `chamada-encerrada`
   * apaga. Um id ausente do mapa significa "sem informação em tempo real" e deixa valer o dado
   * da API; um id com `null` significa "chamada encerrada" e sobrepõe a API, senão um atendente
   * que estava em ligação no momento da busca ficaria com o badge preso até a lista atualizar.
   *
   * Os três eventos são compartilhados com o VoipContext e com o modal de ligação recebida, por
   * isso o bind/unbind aqui é sempre pelo próprio handler.
   */
  useEffect(() => {
    if(!channel) {
      return
    }

    const ehMesmaChamada = (estado, data) => {
      const idChamada = data?.id_chamada ?? data?.idConteudoAtendimento ?? null
      const idAtendimento = data?.id_atendimento ?? data?.id ?? null

      return (idChamada != null && estado?.id_chamada != null && String(estado.id_chamada) === String(idChamada))
        || (idAtendimento != null && estado?.id_atendimento != null && String(estado.id_atendimento) === String(idAtendimento))
    }

    // voip.1: a chamada está tocando para o atendente ofertado.
    const aoOfertarLigacao = ({ data }) => {
      const idAtendente = Number(data?.atendente)
      if(!idAtendente) {
        return
      }
      setEstadosLigacao(estados => ({
        ...estados,
        [idAtendente]: {
          em_ligacao: false,
          recebendo_chamada: true,
          id_chamada: data.id_chamada ?? null,
          id_atendimento: data.id ?? null,
          atendimento_em_ligacao: { nome: data.nome, contato_destino: data.contato }
        }
      }))
    }

    // voip-assumido: quem atendeu passa a em ligação e quem só estava tocando com a mesma
    // chamada perdeu a oferta, então perde o badge junto. Atendente nulo é chamada devolvida
    // à fila: ninguém fica com ela.
    const aoAssumirLigacao = (data) => {
      const idAtendente = Number(data?.atendente) || null
      setEstadosLigacao(estados => {
        const atualizados = Object.fromEntries(
          Object.entries(estados).map(([idUsuario, estado]) => {
            const ehQuemAtendeu = idAtendente && Number(idUsuario) === idAtendente

            return [idUsuario, (!ehQuemAtendeu && ehMesmaChamada(estado, data)) ? null : estado]
          })
        )
        if(idAtendente) {
          const anterior = estados[idAtendente] || {}
          atualizados[idAtendente] = {
            ...anterior,
            em_ligacao: true,
            recebendo_chamada: false,
            id_chamada: data.id_chamada ?? anterior.id_chamada ?? null,
            id_atendimento: data.id_atendimento ?? data.id ?? anterior.id_atendimento ?? null
          }
        }

        return atualizados
      })
    }

    // chamada-encerrada traz idConteudoAtendimento, CallSid, contato e atendido — não traz o
    // atendente. Quem tem estado em tempo real é resolvido pelo id da chamada; para o badge que
    // veio das métricas, sem entrada no mapa, o contato do atendimento é o único vínculo
    // disponível.
    const aoEncerrarChamada = (data) => {
      setEstadosLigacao(estados => {
        const atualizados = Object.fromEntries(
          Object.entries(estados).map(([idUsuario, estado]) => (
            [idUsuario, ehMesmaChamada(estado, data) ? null : estado]
          ))
        )
        Object.entries(metricasLigacaoRef.current).forEach(([idUsuario, metricas]) => {
          const estavaEmLigacaoPelasMetricas = metricas?.em_ligacao || metricas?.recebendo_chamada
          if(atualizados[idUsuario] === undefined && estavaEmLigacaoPelasMetricas
              && ehMesmoContato(metricas.atendimento_em_ligacao?.contato_destino, data?.contato)) {
            atualizados[idUsuario] = null
          }
        })

        return atualizados
      })
    }

    channel.bind('voip.1', aoOfertarLigacao)
    channel.bind('voip-assumido', aoAssumirLigacao)
    channel.bind('chamada-encerrada', aoEncerrarChamada)

    return () => {
      channel.unbind('voip.1', aoOfertarLigacao)
      channel.unbind('voip-assumido', aoAssumirLigacao)
      channel.unbind('chamada-encerrada', aoEncerrarChamada)
    }
  }, [channel])

  return (<Container fluid>
      <DommusBarraTitulo>
        <ItemBarraTitulo>Dashboard</ItemBarraTitulo>
        <ItemBarraTitulo>
          <DatePicker
            onChange={(data) => alterarPeriodo(data, 'dataInicial')}
            value={converteParaData(dataInicialSelecionada)}
            format="dd/MM/yyyy"
            calendarIcon={<Icon icon={calendarOutlineBadged} width="20" style={{ color: '#f58442' }}/>}
            clearIcon={null}
          />
          <DatePicker
            onChange={(data) => alterarPeriodo(data, 'dataFinal')}
            value={converteParaData(dataFinalSelecionada)}
            format="dd/MM/yyyy"
            calendarIcon={<Icon icon={calendarOutlineBadged} width="20" style={{ color: '#f58442' }}/>}
            clearIcon={null}
          />
        </ItemBarraTitulo>
        <OverlayTrigger key="pesquisar-dashboard" placement="top" overlay={<Tooltip id={`tooltip-pesquisa-dashboard`}>Pesquisar</Tooltip>}>
          <ItemBarraTitulo classe="btn" onClick={() => setModalFiltrosAberto(true)}>
            <FontAwesomeIcon icon={faSearch}  color="#265d9c" />
          </ItemBarraTitulo>
        </OverlayTrigger>
      </DommusBarraTitulo>
      { usuarios && usuarios.length ? 
        <DommusToggle toggle={true} title="Atendentes">
            <div className="grid-cards mb-0 px-0">
                {usuarios.map((usuario, idx) => <CartaoDashboardAtendente key={`${usuario}-${idx}`} usuario={usuario} filtros={filtrosAplicados} estadoLigacao={estadosLigacao[usuario.id_usuario]} aoCarregarMetricas={registrarMetricasLigacao}/>)}
            </div>
        </DommusToggle> : <DommusAlertaBootstrap mensagem="Nenhum atendente encontrado."/>}
      <ModalDommus open={modalFiltrosAberto} close={() => setModalFiltrosAberto(false)} size="lg" titulo="Filtrar Atendimentos" classe={'modal-filtros-dashboard'}>
        <Form onSubmit={handleSubmit(atualizarFiltros)} className="pt-3">
          <Row className="mb-3">
            <Col>
              <Form.Label>Última movimentação do atendimento</Form.Label>
              <InputGroup className="mb-3">
                  <Controller
                    name="dataInicial"
                    control={control}
                    render={({ field }) => <Form.Control
                      type="date"
                      value={converteParaInputDate(field.value)}
                      onChange={(evento) => field.onChange(converteParaData(evento.target.value))}
                    />}
                  />
                  <Controller
                    name="dataFinal"
                    control={control}
                    render={({ field }) => <Form.Control
                      type="date"
                      value={converteParaInputDate(field.value)}
                      onChange={(evento) => field.onChange(converteParaData(evento.target.value))}
                    />}
                  />
              </InputGroup>
            </Col>
          </Row>
          <Row>
            <Form.Group as={Col}>
              <Form.Label>Tipo de Atendimento</Form.Label>
              <Controller
                name="tipoAtendimento"
                control={control}
                render={({ field }) => <Select
                    {...field}
                    isMulti
                    placeholder="Selecionar"
                    name="tipoAtendimento"
                    options={montaOpcoes(tiposAtendimento, 'descricao', 'id')}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    menuPosition="fixed"
                />}
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>Empreendimento</Form.Label>
              <Controller
                name="empreendimento"
                control={control}
                render={({ field }) => <Select
                    {...field}
                    isMulti
                    placeholder="Selecionar"
                    name="empreendimento"
                    options={montaOpcoes(empreendimentoLista, 'nome', 'id')}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    menuPosition="fixed"
                />}
              />
            </Form.Group>
          </Row>
          <Row className="formFooter">
            <Col>
              <Button type="submit" className="float-right btn-success"><FontAwesomeIcon icon={faFilter}/> Filtrar</Button>
            </Col>
          </Row>
        </Form>
      </ModalDommus>
    </Container>)
}
